import React, { useEffect, useState } from 'react';
import { Section, Button, FadeIn, SEO, DecorativeBackground } from '../components/UI';
import { Heart, Sparkles, MessageCircle, Users } from 'lucide-react';
import { Link } from 'react-router-dom';

const services = [
  {
    title: 'Sorgsamtaler',
    description: 'Nærværende samtaler og støtte til dig, der har mistet eller står i en svær livskrise.',
    icon: MessageCircle,
    href: '/sorgvejleder',
    image: 'https://assets.auxodev.dk/assets/88d34a44-1e08-49cc-ba2b-c330133a247e/cf56ef26-648e-4f2b-89cb-8ebedfd320ee.jpg'
  },
  {
    title: 'Dødsdoula',
    description: 'Støtte, omsorg og forløb ved livets afslutning for både den døende og de pårørende.',
    icon: Heart,
    href: '/dodsdoula',
    image: 'https://assets.auxodev.dk/assets/88d34a44-1e08-49cc-ba2b-c330133a247e/07d65433-fdfa-4751-b78d-6867f8789661.jpg'
  },
  {
    title: 'Foredrag & Workshops',
    description: 'Inddragende oplæg for vågetjenester og fagfolk samt kreative temadage for skoler og unge.',
    icon: Users,
    href: '/foredrag-workshops',
    image: 'https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/88d34a44-1e08-49cc-ba2b-c330133a247e/workshop-faellesskab-haender-cirkel.jpg'
  }
];

export default function Home() {
  return (
    <div className="pt-0">
      <SEO 
        title="Forside" 
        description="Nærværende samtaler og støtte til mennesker i sorg, livskriser og ved livets afslutning. Sorgvejledning og dødsdoula i rolige omgivelser."
      />
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://assets.auxodev.dk/assets/88d34a44-1e08-49cc-ba2b-c330133a247e/1ef5862d-54ab-42c7-bddc-b6510cc194ce.jpg" 
            alt="Roligt dansk hjem" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-sorg-sand/40 backdrop-blur-[2px]"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-sorg-sand/20 to-sorg-sand"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center px-6">
          <FadeIn direction="none">
            <h1 className="text-4xl md:text-7xl font-serif text-sorg-dark mb-6 leading-tight">
              Støtte gennem livets <br className="hidden md:block" /> svære overgange
            </h1>
            <p className="text-lg md:text-xl text-sorg-dark/70 mb-10 max-w-2xl mx-auto leading-relaxed">
              Nærværende samtaler og støtte til mennesker i sorg, livskriser og ved livets afslutning.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button to="/kontakt">Kontakt mig</Button>
              <Button to="/sorgvejleder" variant="secondary">Læs mere om forløb</Button>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Intro Section */}
      <Section className="bg-transparent relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <FadeIn direction="right">
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-32 h-32 bg-sorg-beige/50 organic-shape -z-10"></div>
              <img 
                src="https://assets.auxodev.dk/assets/88d34a44-1e08-49cc-ba2b-c330133a247e/2b1f682b-0699-4d52-bcd9-588e202a8442.jpg" 
                alt="Smuk lotus - Sorgskab" 
                className="w-full h-auto object-contain rounded-2xl soft-shadow"
              />
              <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-sorg-sage/10 organic-shape -z-10"></div>
            </div>
          </FadeIn>
          <FadeIn direction="left">
            <span className="text-sorg-earth font-medium tracking-widest uppercase text-xs mb-4 block">Om Sorgskab</span>
            <h2 className="text-4xl md:text-5xl font-serif text-sorg-dark mb-8 leading-tight">
              Et rum for det, der <br /> er svært at bære
            </h2>
            <div className="space-y-6 text-sorg-dark/70 leading-relaxed text-lg">
              <p>
                Jeg hedder Asha, og jeg har skabt Sorgskab som et trygt og værdigt rum for dig, der står midt i en af livets store forandringer. 
              </p>
              <p>
                Uanset om du har mistet en nærtstående, står i en skilsmisse eller mærker en eksistentiel krise, så er mit fokus altid på det menneskelige møde. Her er der plads til hele dig – uden krav om behandling, men med fokus på nærvær og støtte.
              </p>
              <Button to="/om-asha" variant="outline" className="mt-4">Læs min historie</Button>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Focus Areas */}
      <Section className="bg-white/40 backdrop-blur-sm relative">
        <div className="text-center mb-20">
          <FadeIn>
            <h2 className="text-4xl font-serif text-sorg-dark mb-4">Hvordan jeg kan støtte dig</h2>
            <p className="text-sorg-dark/60 max-w-xl mx-auto">
              Jeg tilbyder forskellige former for støtte, tilpasset din situation og dine behov.
            </p>
          </FadeIn>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {services.map((service, index) => (
            <FadeIn 
              key={service.title} 
              delay={index * 0.1}
            >
              <Link to={service.href} className="group block h-full">
                <div className="bg-white/60 backdrop-blur-sm rounded-3xl overflow-hidden h-full transition-all duration-500 hover:soft-shadow hover:-translate-y-1 border border-transparent hover:border-sorg-beige">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img 
                      src={service.image} 
                      alt={service.title} 
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-8">
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-sorg-earth mb-6 soft-shadow">
                      <service.icon size={24} strokeWidth={1} />
                    </div>
                    <h3 className="text-2xl font-serif text-sorg-dark mb-4">{service.title}</h3>
                    <p className="text-sorg-dark/60 leading-relaxed mb-6">
                      {service.description}
                    </p>
                    <span className="text-sorg-earth text-sm font-medium flex items-center group-hover:translate-x-1 transition-transform">
                      Læs mere <span className="ml-2">→</span>
                    </span>
                  </div>
                </div>
              </Link>
            </FadeIn>
          ))}
        </div>
      </Section>

      {/* Trust Section */}
      <Section className="bg-sorg-beige/20 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto text-center">
          <FadeIn>
            <div className="w-20 h-20 bg-white organic-shape flex items-center justify-center text-sorg-earth mx-auto mb-8 soft-shadow">
              <Heart size={32} strokeWidth={1} />
            </div>
            <h2 className="text-4xl md:text-5xl font-serif text-sorg-dark mb-8">Du behøver ikke stå alene</h2>
            <p className="text-xl text-sorg-dark/70 leading-relaxed italic mb-10">
              "Det kræver mod at række ud, når alt føles tungt. Men i mødet med et andet menneske kan vi finde den ro og værdighed, der skal til for at bevæge os videre."
            </p>
            <Button to="/kontakt">Tag kontakt i dag</Button>
          </FadeIn>
        </div>
      </Section>

      {/* Final CTA */}
      <Section className="bg-sorg-earth text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 organic-shape -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 organic-shape translate-y-1/2 -translate-x-1/2"></div>
        
        <div className="relative z-10 text-center max-w-2xl mx-auto">
          <FadeIn>
            <h2 className="text-4xl md:text-5xl font-serif mb-8">Tag kontakt når du er klar</h2>
            <p className="text-lg text-white/80 mb-10 leading-relaxed">
              Alle henvendelser er velkomne. Jeg svarer typisk inden for 24 timer og tilbyder altid en uforpligtende indledende samtale.
            </p>
            <Button to="/kontakt" className="bg-white text-sorg-earth hover:bg-sorg-sand">
              Lad os tale sammen
            </Button>
          </FadeIn>
        </div>
      </Section>
    </div>
  );
}
